import { Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { Toaster } from '@tokeet/tv3-platform'
import * as R from 'ramda'
import { AuthService } from '@tv3/services/auth.service'
import { AppConfig } from '@tv3/app.config'
import { addAttachmentsComplete, Attachment } from '@tokeet/tv3-platform'
import { FileItem, FileUploader } from 'ng2-file-upload'
import { CustomFileUploaderOptions, FileUploaderFactoryService } from '@tv3/services/file-uploader-factory.service'
import { FileSizePipe } from '@tokeet/tv3-platform'

export interface UploadFileOptions {
  maxSize?: number
  maxUploads?: number
  allowedFileExtensions?: string[]
  title?: string
  guide?: string
  cloudinary?: boolean
}

@Component({
  selector: 'app-upload-file-dialog',
  templateUrl: './upload-file-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./upload-file-dialog.component.scss'],
})
export class UploadFileDialogComponent {
  options: UploadFileOptions = {
    title: 'Upload Files',
    maxSize: 10 * 1024 * 1024, // 10MB,
    maxUploads: 100,
    cloudinary: false,
  }
  uploader: FileUploader
  files: FileItem[] = []
  uploadedFileResults: any[] = []
  acceptFileExtensions: string
  isUploading = false

  private fileSizePipe = new FileSizePipe()

  constructor(
    public dialogRef: MatDialogRef<UploadFileDialogComponent>,
    private store: Store<fromRoot.State>,
    private authService: AuthService,
    private appConfig: AppConfig,
    private fileUploaderFactory: FileUploaderFactoryService,
    @Inject(MAT_DIALOG_DATA) public data: { options: UploadFileOptions },
    protected toast: Toaster
  ) {
    this.options = { ...this.options, ...this.data.options }
    this.options.guide = this.getGuideText()

    if (!this.options.allowedFileExtensions && this.options.cloudinary) {
      this.options.allowedFileExtensions = ['.png', '.jpg', '.jpeg', '.jfif']
    }

    if (this.options.allowedFileExtensions) {
      this.acceptFileExtensions = this.options.allowedFileExtensions.join(',')
    }

    this.uploader = this.fileUploaderFactory.create(
      this.options.cloudinary
        ? this.getCloudinaryUploadOptions(this.options)
        : this.getTokeetUploadOptions(this.options),
      {
        onAfterAddingFile: async (fileItem) => {
          if (this.options.cloudinary) {
            fileItem.withCredentials = false
          }
          this.files.push(fileItem)
        },
        onCompleteItem: (_fileItem, res) => {
          if (res) {
            this.uploadedFileResults = this.uploadedFileResults.concat(JSON.parse(res))
          }
        },
        onCompleteAll: () => {
          if (this.uploadedFileResults.length === this.files.length) {
            const attachments = R.map(Attachment.deserialize, this.uploadedFileResults)
            if (!this.options.cloudinary) {
              this.store.dispatch(addAttachmentsComplete({ attachments }))
            }
            this.toast.success('File(s) uploaded successfully.')
            this.dialogRef.close(attachments)
          }
        },
      }
    )
  }

  getTokeetUploadOptions(options: UploadFileOptions): CustomFileUploaderOptions {
    return {
      url: `${this.appConfig.apiUrl}/attachment`,
      autoUpload: false,
      maxFileSize: options.maxSize,
      queueLimit: options.maxUploads,
      allowedFileExtensions: options.allowedFileExtensions,
      authToken: this.authService.token,
    }
  }

  getCloudinaryUploadOptions(options: UploadFileOptions): CustomFileUploaderOptions {
    return {
      url: `${this.appConfig.config.cloudinaryURL}/${this.appConfig.config.cloudinaryCloudName}/upload`,
      allowedFileExtensions: this.acceptFileExtensions.split(','),
      autoUpload: false,
      maxFileSize: options.maxSize,
      queueLimit: options.maxUploads,
      additionalParameter: {
        upload_preset: this.appConfig.config.cloudinaryUploadPreset,
        tags: 'ablum',
        context: 'photo=' + ('title' || ''),
      },
    }
  }

  getGuideText() {
    if (this.options.guide) {
      return this.options.guide
    }
    return `You can upload <b>${
      this.options.maxUploads
    }</b> file(s) at one time, and file size should be less than <b>${this.fileSizePipe.transform(
      this.options.maxSize,
      0
    )}</b>.`
  }

  onRemoveFile(file: FileItem, index) {
    this.uploader.removeFromQueue(file)
    this.files.splice(index, 1)
  }

  close() {
    this.dialogRef.close()
  }

  create() {
    this.isUploading = true
    this.uploader.uploadAll()
  }
}
